'use client';

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import * as React from 'react';

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';

const ListItem = React.forwardRef<
  React.ElementRef<typeof Link>,
  React.ComponentPropsWithoutRef<typeof Link>
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          ref={ref}
          className={cn(
            'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-zinc-200 hover:text-primary focus:bg-zinc-200 focus:text-primary',
            className
          )}
          {...props}
        >
          <div className="font-bold text-base leading-none">{title}</div>
          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">{children}</p>
        </Link>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = 'ListItem';

export function MegaMenu({ isInjectable = false }: { isInjectable?: boolean }) {
  const t = useTranslations('nav.megaMenu');
  const linkTarget = isInjectable ? '_parent' : undefined;
  const baseId = React.useId();

  return (
    <NavigationMenu className="h-full items-center justify-start">
      <NavigationMenuList className="h-full items-center gap-2 sm:gap-4 lg:gap-8 [&_li]:mb-0">
        <NavigationMenuItem value={`item-${baseId}-1`} className="!mb-0 flex">
          <NavigationMenuTrigger
            aria-label="Automate menu"
            className="!text-black hover:!text-primary flex h-12 cursor-pointer items-center justify-center rounded-md bg-transparent px-2 font-bold text-[1.2rem] transition-colors hover:bg-zinc-200 focus:bg-zinc-200 data-[state=open]:bg-zinc-200 data-[state=open]:text-primary md:px-3 lg:px-4"
          >
            Automate
          </NavigationMenuTrigger>
          <NavigationMenuContent className="border border-gray-200 bg-white shadow-lg">
            <ul className="[&_li]:!mb-0 grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[1fr_1fr]">
              <ListItem href="/#automate" title="ZapFlow" target={linkTarget}>
                Business Process Automation
              </ListItem>
              <ListItem href="/calculator" title="ROI Calculator" target={linkTarget}>
                Calculate your savings
              </ListItem>
              <ListItem href="/#getstarted" title="Screen Recording" target={linkTarget}>
                Record your screen
              </ListItem>
              <ListItem href="/#contact" title="Book a Demo" target={linkTarget}>
                Schedule a demo
              </ListItem>
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
        <NavigationMenuItem value={`item-${baseId}-2`} className="!mb-0 flex">
          <NavigationMenuTrigger
            aria-label="Create menu"
            className="!text-black hover:!text-primary flex h-12 cursor-pointer items-center justify-center rounded-md bg-transparent px-2 font-bold text-[1.2rem] transition-colors hover:bg-zinc-200 focus:bg-zinc-200 data-[state=open]:bg-zinc-200 data-[state=open]:text-primary md:px-3 lg:px-4"
          >
            Create
          </NavigationMenuTrigger>
          <NavigationMenuContent className="border border-gray-200 bg-white shadow-lg">
            <div className="grid gap-6 p-6 md:w-[500px] lg:w-[600px]">
              {/* VIDEOS Section */}
              <div>
                <div className="mb-2 px-3 font-bold text-gray-400 text-xs uppercase tracking-widest">
                  VIDEO GENERATION
                </div>
                <ul className="grid gap-3 lg:grid-cols-2">
                  <ListItem
                    href="/grok-imagine-ai-video-generator"
                    title="Grok Imagine"
                    target={linkTarget}
                  >
                    Elite AI video generation
                  </ListItem>
                  <ListItem
                    href="/grok-imagine-ai-video-generator#explore"
                    title="Explore Grok Imagine"
                    target={linkTarget}
                  >
                    Browse Grok videos
                  </ListItem>
                  <ListItem
                    href="/runway-gen-4-ai-video-generator"
                    title="Runway Gen-4"
                    target={linkTarget}
                  >
                    Cinema-quality AI video
                  </ListItem>
                  <ListItem
                    href="/runway-gen-4-ai-video-generator#explore"
                    title="Explore Runway Gen-4"
                    target={linkTarget}
                  >
                    Professional video gallery
                  </ListItem>
                </ul>
              </div>

              {/* IMAGES Section */}
              <div>
                <div className="mb-2 px-3 font-bold text-gray-400 text-xs uppercase tracking-widest">
                  IMAGE GENERATION
                </div>
                <ul className="grid gap-3 lg:grid-cols-2">
                  <ListItem
                    href="/nano-banana-2-ai-image-generator"
                    title="Nano Banana 2"
                    target={linkTarget}
                  >
                    Google&apos;s latest image model
                  </ListItem>
                  <ListItem
                    href="/nano-banana-2-ai-image-generator#explore"
                    title="Explore Nano Banana 2"
                    target={linkTarget}
                  >
                    Discover new generations
                  </ListItem>
                  <ListItem href="/z-image-ai-image-generator" title="Z-Image" target={linkTarget}>
                    High-quality AI images
                  </ListItem>
                  <ListItem
                    href="/z-image-ai-image-generator#explore"
                    title="Explore Z-Image"
                    target={linkTarget}
                  >
                    Browse community images
                  </ListItem>
                </ul>
              </div>

              {/* BOTTOM Section */}
              <div className="border-gray-100 border-t pt-4">
                <ul className="grid gap-3 lg:grid-cols-1">
                  <ListItem href="/pricing" title="Pricing" target={linkTarget}>
                    Simple, transparent pricing for all models
                  </ListItem>
                </ul>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
        <NavigationMenuItem value={`item-${baseId}-3`} className="!mb-0 flex">
          <NavigationMenuTrigger
            aria-label="Communicate menu"
            className="!text-black hover:!text-primary flex h-12 cursor-pointer items-center justify-center rounded-md bg-transparent px-2 font-bold text-[1.2rem] transition-colors hover:bg-zinc-200 focus:bg-zinc-200 data-[state=open]:bg-zinc-200 data-[state=open]:text-primary md:px-3 lg:px-4"
          >
            Communicate
          </NavigationMenuTrigger>
          <NavigationMenuContent className="border border-gray-200 bg-white shadow-lg">
            <ul className="[&_li]:!mb-0 grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[1fr_1fr]">
              <ListItem href="/#communicate" title="ZapChat" target={linkTarget}>
                Smart AI Chatbots
              </ListItem>
              <ListItem href="/#communicate" title="ZapVoice" target={linkTarget}>
                AI Voice Agents
              </ListItem>
              <ListItem href="/#communicate" title="ZapVideo" target={linkTarget}>
                Automated Video Publishing
              </ListItem>
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
